import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Datatable} from '../../../components/Datatable/Datatable'
import type {MetaType} from '../../../components/Datatable/Datatable'
import {Button} from '../../../components/Button/Button'
import useModals from '../../../hooks/useModals'
import {DELETE, POST, PUT} from '../../../services/api'
import {Loading} from '../../../components/Loading/Loading'
import {ModalDelete} from '../../../components/Modals/General/ModalDelete/ModalDelete'
import {useNavigate} from 'react-router-dom'
import Swal from 'sweetalert2'
import {i18n} from '../../../translate/i18n'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {
  ReservationRoomStatus,
  HotelReservationType,
  getReservationStatusLabel,
  formatDate,
} from './types'
import {Input} from '../../../components/Input/Input'

// Estilos para o modal de checkout
const checkoutModalStyles = `
<style>
.checkout-confirmation-modal {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
.checkout-confirmation-modal .swal2-html-container {
  text-align: left;
  line-height: 1.6;
}
.checkout-confirmation-modal ul {
  margin: 10px 0;
  padding-left: 20px;
}
.checkout-confirmation-modal li {
  margin: 5px 0;
  list-style-type: disc;
}
.checkout-confirmation-modal input[type="checkbox"] {
  margin-right: 10px;
  transform: scale(1.2);
}
.checkout-confirmation-modal label {
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-top: 15px;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 5px;
  border: 2px solid #dee2e6;
  transition: all 0.3s ease;
}
.checkout-confirmation-modal label:hover {
  background-color: #e9ecef;
  border-color: #adb5bd;
}
.checkout-confirmation-modal input[type="checkbox"]:checked + span {
  color: #198754;
  font-weight: bold;
}
</style>
`

const HotelRoomReservationContent: FC = () => {
  const navigate = useNavigate()
  const {showDeleteModal} = useModals()
  const [loading, setLoading] = useState(false)
  const [reservations, setReservations] = useState<HotelReservationType[]>([])
  const [id, setId] = useState<number | null>(null)
  const [, setMeta] = useState<MetaType | undefined>(undefined)

  // Filtros organizados em um objeto como no schedulesIndex
  const [filters, setFilters] = useState({
    status: '',
    checkInDate: '',
    checkOutDate: '',
    roomType: '',
    customerName: '',
  })
  const [roomTypes] = useState<{value: string; select: string}[]>([
    {
      value: 'single',
      select: 'Single',
    },
    {
      value: 'double',
      select: 'Double',
    },
    {
      value: 'twin',
      select: 'Twin',
    },
    {
      value: 'queen',
      select: 'Queen',
    },
    {
      value: 'king',
      select: 'King',
    },
    {
      value: 'suite',
      select: 'Suite',
    },
    {
      value: 'junior_suite',
      select: 'Junior Suite',
    },
    {
      value: 'master_suite',
      select: 'Master Suite',
    },
    {
      value: 'presidential',
      select: 'Presidential',
    },
    {
      value: 'family',
      select: 'Family',
    },
    {
      value: 'accessible',
      select: 'Accessible',
    },
  ])

  const getReservations = async (searchFilters?: any) => {
    try {
      setLoading(true)

      // Usar filtros passados como parâmetro ou os filtros do estado
      const filtersToUse = searchFilters || filters

      // Construir payload para o filtro (remover campos vazios)
      const filterPayload: any = {}

      if (filtersToUse.status) filterPayload.status = filtersToUse.status
      if (filtersToUse.checkInDate) filterPayload.checkInStart = filtersToUse.checkInDate
      if (filtersToUse.checkOutDate) filterPayload.checkOutEnd = filtersToUse.checkOutDate
      if (filtersToUse.roomType) filterPayload.roomType = filtersToUse.roomType

      const res = await POST('/hotel-reservation/filter', filterPayload)

      const formattedReservations = res.dados.map((reservation: any) => ({
        id: `${reservation.id}`,
        idExt: `#${reservation.id}`,
        roomId: reservation.roomId,
        customerId: reservation.customerId,
        checkIn: reservation.checkIn,
        checkOut: reservation.checkOut,
        status: reservation.status,
        pluginspaceId: reservation.pluginspaceId,
        roomNumber: reservation.room?.roomNumber || `Quarto ${reservation.roomId}`,
        customerName: reservation.customer?.name || 'Cliente não informado',
        customerEmail: reservation.customer?.email || '',
        customerPhone: reservation.customer?.phone || '',
        roomType: reservation.room?.type || '',
        totalPrice: reservation.totalPrice || 0,
        statusLabel: getReservationStatusLabel(reservation.status),
        checkInFormatted: formatDate(reservation.checkIn),
        checkOutFormatted: reservation.checkOut ? formatDate(reservation.checkOut) : 'Não definido',
        priceFormatted: reservation.totalPrice ? `R$ ${reservation.totalPrice.toFixed(2)}` : '-',
        createdAt: reservation.createdAt,
        updatedAt: reservation.updatedAt,
      }))

      setReservations(formattedReservations)
      setMeta(res.meta)

      setLoading(false)
    } catch (e) {
      setLoading(false)
      setReservations([])
      console.log(e)
    }
  }

  const deleteReservation = async () => {
    try {
      setLoading(true)
      const gridRes = await DELETE(`/hotel-reservation/${id}`)
      if (gridRes.success === true) {
        Swal.fire({
          icon: 'success',
          title: 'Reserva deletada',
          text: 'Deleção realizada com sucesso!',
        }).then(async () => await getReservations())
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Erro ao deletar a reserva!',
        }).then(async () => await getReservations())
      }
      setLoading(false)
    } catch (e) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Erro ao deletar a reserva!',
      }).then(async () => await getReservations())
      console.log(e)
    }
  }

  const handleFilterChange = (field: keyof typeof filters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSearch = () => {
    getReservations(filters)
  }

  const updateReservationStatus = async (
    reservationId: number,
    newStatus: ReservationRoomStatus
  ) => {
    try {
      setLoading(true)
      const response = await PUT(`/hotel-reservation/${reservationId}`, {status: newStatus})

      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: 'Status atualizado',
          text: 'Status da reserva atualizado com sucesso!',
        }).then(async () => await getReservations())
      } else {
        throw new Error(response.message || 'Erro ao atualizar status')
      }

      setLoading(false)
    } catch (e: any) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: e || 'Erro ao atualizar o status da reserva!',
      })
      console.log(e)
    }
  }

  const handleStatusChange = (
    reservation: HotelReservationType,
    newStatus: ReservationRoomStatus
  ) => {
    const statusName = getReservationStatusLabel(newStatus)

    Swal.fire({
      title: 'Confirmar alteração',
      text: `Deseja alterar o status da reserva para "${statusName}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sim, alterar',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (result.isConfirmed) {
        updateReservationStatus(reservation.id, newStatus)
      }
    })
  }

  const updateReservationCheckInCheckOutStatus = async (
    reservationId: number,
    newStatus: ReservationRoomStatus,
    type: 'checkIn' | 'checkOut'
  ) => {
    try {
      setLoading(true)
      let payload: any = {status: newStatus}
      let response = {
        success: false,
        data: {},
        message: '',
      }

      if (type === 'checkIn') {
        payload.checkIn = new Date()
        response = await PUT(`/hotel-reservation/${reservationId}`, payload)
      } else {
        response = await POST(`/hotel-reservation/checkout/${reservationId}`)
      }

      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: 'Status atualizado',
          text: 'Status da reserva atualizado com sucesso!',
        }).then(async () => await getReservations())
      } else {
        throw new Error(response.message || 'Erro ao atualizar status')
      }

      setLoading(false)
    } catch (e: any) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: e || 'Erro ao atualizar o status da reserva!',
      })
      console.log(e)
    }
  }

  const handleCheckInCheckOutStatusChange = (
    reservation: HotelReservationType,
    newStatus: ReservationRoomStatus,
    type: 'checkIn' | 'checkOut'
  ) => {
    // const statusName = getReservationStatusLabel(newStatus)

    if (type === 'checkIn') {
      Swal.fire({
        title: 'Confirmar Check-in',
        text: 'Deseja fazer o check-in para esta reserva?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sim, fazer check-in',
        cancelButtonText: 'Cancelar',
      }).then((result) => {
        if (result.isConfirmed) {
          updateReservationCheckInCheckOutStatus(reservation.id, newStatus, type)
        }
      })
    } else {
      // Para checkout, mostrar modal com confirmação de leitura
      Swal.fire({
        title: 'Confirmar Check-out',
        html: `
          ${checkoutModalStyles}
          <div style="text-align: left; margin: 20px 0;">
            <p><strong>⚠️ ATENÇÃO - LEIA ATENTAMENTE:</strong></p>
            <p>Ao fazer o check-out, a reserva será <strong>concluída</strong> e o cliente <strong>não poderá mais realizar o pagamento pelo aplicativo</strong>.</p>
            <p><strong>Antes de prosseguir, confirme se:</strong></p>
            <ul style="text-align: left; margin: 10px 0; padding-left: 20px;">
              <li>O cliente já efetuou o pagamento</li>
              <li>Não há pendências financeiras</li>
              <li>Todos os serviços foram devidamente quitados</li>
            </ul>
            <hr style="margin: 15px 0;">
            <label style="display: flex; align-items: center; cursor: pointer; margin-top: 15px;">
              <input type="checkbox" id="confirmRead" style="margin-right: 10px; transform: scale(1.2);">
              <span><strong>Li e confirmo que todas as validações foram verificadas</strong></span>
            </label>
          </div>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Confirmar Check-out',
        cancelButtonText: 'Cancelar',
        preConfirm: () => {
          const checkbox = document.getElementById('confirmRead') as HTMLInputElement
          if (!checkbox?.checked) {
            Swal.showValidationMessage(
              'Você deve confirmar que leu e verificou todas as validações antes de prosseguir.'
            )
            return false
          }
          return true
        },
        customClass: {
          popup: 'checkout-confirmation-modal',
        },
      }).then((result) => {
        if (result.isConfirmed) {
          // Confirmação final
          Swal.fire({
            title: 'Última confirmação',
            text: 'Tem certeza que deseja finalizar o check-out? Esta ação não pode ser desfeita.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Sim, finalizar check-out',
            cancelButtonText: 'Cancelar',
          }).then((finalResult) => {
            if (finalResult.isConfirmed) {
              updateReservationCheckInCheckOutStatus(reservation.id, newStatus, type)
            }
          })
        }
      })
    }
  }

  useEffect(() => {
    getReservations()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t('hospitality_room_reservation.title')}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t('hospitality_room_reservation.subtitle')}
                </span>
              </h3>
              <div className='card-toolbar'>
                <Button
                  text={i18n.t('hospitality_room_reservation.btn_new_reservation')}
                  color='primary'
                  iconColor='#FFF'
                  icon='fas fa-plus'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={() => navigate('criar')}
                  btnClass='mb-1'
                />
              </div>
            </div>

            {/* Seção de Filtros Sempre Visível */}
            <div className='card-body border-bottom'>
              <div className='row g-3'>
                <div className='col-md-3'>
                  <Input
                    inputAttr={{type: 'select'}}
                    label={i18n.t('hospitality_room_reservation.filter_status_label')}
                    value={filters.status}
                    change={(value: string) => handleFilterChange('status', value)}
                    options={[
                      {value: '', select: i18n.t('hospitality_room_reservation.filter_status_all')},
                      ...Object.values(ReservationRoomStatus).map((status) => ({
                        value: status,
                        select: getReservationStatusLabel(status),
                      })),
                    ]}
                    placeholder={i18n.t('hospitality_room_reservation.filter_status_placeholder')}
                  />
                </div>

                <div className='col-md-3'>
                  <Input
                    inputAttr={{type: 'date'}}
                    label='Data Check-in (A partir de)'
                    value={filters.checkInDate}
                    change={(value: string) => handleFilterChange('checkInDate', value)}
                    placeholder='Selecionar data'
                  />
                </div>

                <div className='col-md-3'>
                  <Input
                    inputAttr={{type: 'date'}}
                    label='Data Check-out (Até)'
                    value={filters.checkOutDate}
                    change={(value: string) => handleFilterChange('checkOutDate', value)}
                    placeholder='Selecionar data'
                  />
                </div>

                <div className='col-md-3'>
                  <Input
                    inputAttr={{type: 'select'}}
                    label='Tipo de Quarto'
                    value={filters.roomType}
                    change={(value: string) => handleFilterChange('roomType', value)}
                    options={[{value: '', select: 'Todos os tipos'}, ...roomTypes]}
                    placeholder='Selecionar tipo'
                  />
                </div>

                <div className='d-flex gap-2 align-items-end h-100 justify-content-end'>
                  <Button text='Buscar' color='primary' size='small' click={handleSearch} />
                </div>
              </div>
            </div>

            <div className='card-body'>
              <Datatable
                data={reservations}
                headers={[
                  {
                    key: i18n.t('hospitality_room_reservation.table_reservation'),
                    value: 'id',
                    type: 'default',
                  },
                  {
                    key: i18n.t('hospitality_room_reservation.table_room'),
                    value: 'roomNumber',
                    type: 'default',
                  },
                  {
                    key: i18n.t('hospitality_room_reservation.table_customer'),
                    value: 'customerName',
                    type: 'default',
                  },
                  {
                    key: i18n.t('hospitality_room_reservation.table_checkin'),
                    value: 'checkInFormatted',
                    type: 'default',
                  },
                  {
                    key: i18n.t('hospitality_room_reservation.table_checkout'),
                    value: 'checkOutFormatted',
                    type: 'default',
                  },
                  {
                    key: i18n.t('hospitality_room_reservation.table_status'),
                    value: 'statusLabel',
                    type: 'default',
                  },
                  {
                    key: i18n.t('hospitality_room_reservation.table_price'),
                    value: 'priceFormatted',
                    type: 'default',
                  },
                  {
                    key: i18n.t('hospitality_room_reservation.table_actions'),
                    value: 'acoes',
                    type: '',
                  },
                ]}
                options={[
                  {
                    icon: 'fas fa-eye',
                    action: (item) => {
                      navigate(`editar/${item.id}`)
                    },
                    title: i18n.t('hospitality_room_reservation.btn_details'),
                  },
                  {
                    icon: 'fas fa-check',
                    action: (item: HotelReservationType) =>
                      handleStatusChange(item, ReservationRoomStatus.CONFIRMED),
                    title: i18n.t('hospitality_room_reservation.btn_confirm'),
                  },
                  {
                    icon: 'fas fa-sign-in-alt',
                    action: (item: HotelReservationType) =>
                      handleCheckInCheckOutStatusChange(
                        item,
                        ReservationRoomStatus.IN_PROGRESS,
                        'checkIn'
                      ),
                    title: i18n.t('hospitality_room_reservation.btn_checkin'),
                  },
                  {
                    icon: 'fas fa-sign-out-alt',
                    action: (item: HotelReservationType) =>
                      handleCheckInCheckOutStatusChange(
                        item,
                        ReservationRoomStatus.COMPLETED,
                        'checkOut'
                      ),
                    title: i18n.t('hospitality_room_reservation.btn_checkout'),
                  },
                  {
                    icon: 'fas fa-times',
                    action: (item: HotelReservationType) =>
                      handleStatusChange(item, ReservationRoomStatus.CANCELLED),
                    title: i18n.t('hospitality_room_reservation.btn_cancel'),
                  },
                  {
                    icon: 'fas fa-user-times',
                    action: (item: HotelReservationType) =>
                      handleStatusChange(item, ReservationRoomStatus.NO_SHOW),
                    title: i18n.t('hospitality_room_reservation.btn_no_show'),
                  },
                  {
                    icon: 'fas fa-trash',
                    action: (item) => {
                      showDeleteModal(i18n.t('hospitality_room_reservation.msg_confirm_delete'))
                      setId(item.id)
                    },
                    title: i18n.t('hospitality_room_reservation.btn_delete'),
                  },
                ]}
                color='primary'
              />
            </div>
          </div>

          <ModalDelete confirmAction={() => deleteReservation()} />
        </div>
      )}
    </>
  )
}

const HotelRoomReservation: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: i18n.t('hospitality_room_reservation.breadcrumb_dashboard'),
            path: 'dashboard',
            isActive: false,
          },
          {
            title: i18n.t('hospitality_room_reservation.breadcrumb_reservation'),
            path: 'hotel-reservation',
            isActive: true,
          },
        ]}
      >
        {i18n.t('hospitality_room_reservation.title')}
      </PageTitle>
      <HotelRoomReservationContent />
    </>
  )
}

export {HotelRoomReservation}
